<template>
  <section class="ATable" :id="id">
    <div class="flex" v-if="!hideTopPanel">
      <div class="lt-area flex-auto">
        <div class="inline">
          <el-input
            v-model="searchWord"
            v-if="search_field"
            class="input-with-select"
            :size="searchSize"
            :placeholder="placeholder"
            :style="{ width: searchWidth }"
            @keydown.enter.native="handleClickSearch"
          >
            <el-button
              slot="append"
              icon="el-icon-search"
              @click="handleClickSearch"
            />
          </el-input>
        </div>
        <div :class="classSearchContainer" class="search-container inline">
          <slot name="search" />
        </div>
      </div>
      <div class="rt-area flex-fixed align-right" v-if="!hideButtonExcel">
        <el-button
          icon="fas fa-file-excel fa-lg"
          style="padding: 8px;"
          :disabled="disabled"
          circle
          @click="handleClickExcel"
        ></el-button>
      </div>
    </div>
    <div class="row-splitter">
      <el-table
        ref="table"
        v-loading="loading"
        :class="tableClass"
        :row-key="rowKey"
        :data="tableData"
        :height="height"
        :border="border"
        :max-height="maxHeight"
        :highlight-current-row="highlightCurrentRow"
        :row-class-name="rowClassName"
        :size="size"
        :span-method="spanMethod"
        :show-summary="showSummary"
        :summary-method="summaryMethod ? summaryMethod : getSummaries"
        :lazy="lazy"
        default-expand-all
        @cell-click="handleCellClick"
        @row-click="handleClickRow"
        @select="handleSelect"
        @selection-change="handleSelectionChange"
        @current-change="handleCurrentChange"
        @sort-change="handleSortChange"
      >
        <slot></slot>
      </el-table>
    </div>
    <div class="flex row-splitter">
      <div class="lb-area flex-fixed" :style="{ width: buttonsWidth }">
        <el-button
          type="primary"
          v-if="!hideButtonInsert"
          v-shortkey="['f2']"
          :disabled="disable_insert"
          @shortkey.native="handleClickCreate"
          @click="handleClickCreate"
          >추가 (F2)</el-button
        >
        <slot name="buttons" />
      </div>
      <div class="flex-auto align-center">
        <el-pagination
          background
          layout="prev, pager, next"
          v-if="!hidePagination"
          :page-size="pageSize"
          :total="recordCount"
          :current-page.sync="currentPage"
          @current-change="handleCurrentPageChange"
        ></el-pagination>
      </div>
      <div class="rb-area flex-fixed" :style="{ width: buttonsWidth }">
        <slot name="right-bottom" />
      </div>
    </div>
  </section>
</template>

<script>
import export2csv from "@/common/function/export2csv";

let uniqueId = 0;

export default {
  name: "ATable",
  data() {
    return {
      searchWord: "",
      fixedSearchWord: "",
      currentPage: 1,
      filteredData: [],
      id: undefined,
      autoMaxHeight: undefined,
      is_shift: false,
      last_selection_index: -1,
      // 마지막 선택 상태가 선택되었는지 해제되었는지 저장
      last_selected: false,
      table_index: 0,
    };
  },
  props: {
    data: { default: [] },
    rowKey: { type: String, default: "id" },
    loading: { type: Boolean, default: false },
    searchSize: { type: String, default: "mini" },
    searchWidth: { type: String, default: "300px" },
    classSearchContainer: null,
    placeholder: { type: String, default: " " },
    search_field: { type: null },
    height: [String, Number],
    maxHeight: [String, Number],
    pageSize: { type: Number, default: 25 },
    exportName: { type: String },
    hideButtonExcel: { type: Boolean, default: false },
    hideButtonInsert: { type: Boolean, default: false },
    hidePagination: { type: Boolean, default: false },
    autoHeight: { type: Boolean, default: false },
    buttonsWidth: { type: String, default: "100px" },
    highlightCurrentRow: { type: Boolean, default: false },
    hideTopPanel: { type: Boolean, default: false },
    border: { type: Boolean, default: false },
    size: { type: String, default: "small" },
    spanMethod: Function,
    rowClassName: [String, Function],
    showSummary: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    disable_insert: { type: Boolean, default: false },
    exportExcel: Function,
    tableClass: { type: String, default: "table-height__common" },
    lazy: { type: Boolean, default: false },
    summaryFields: {
      type: Array,
      default: function() {
        return [];
      },
    },
    summaryTitleColumnIndex: { type: Number, default: 0 },
    summaryMethod: { type: Function },
  },
  computed: {
    columns() {
      return this.$refs.table.columns;
    },
    filterData() {
      let items = this.util.filterMultiFields(
        this.data,
        this.search_field,
        this.fixedSearchWord
      );
      return items;
    },
    recordCount() {
      if (!this.filterData) {
        return 0;
      }
      return this.filterData.length;
    },
    tableData() {
      let items = this.filterData;
      if (!items || items.length < 1) {
        return items;
      }
      if (!this.hidePagination) {
        return items.slice(
          (this.currentPage - 1) * this.pageSize,
          this.pageSize * this.currentPage
        );
      } else {
        return items;
      }
    },
  },
  created() {
    this.id = "ATable_" + uniqueId++;
  },
  mounted() {
    this.$nextTick(function() {
      document.addEventListener("keydown", this.handleKeydown);
      document.addEventListener("keyup", this.handleKeyup);
      window.addEventListener("resize", this.getWidowHeight);
      this.getWidowHeight();
    });
  },
  updated() {
    this.util.fireResize();
  },
  destroyed() {
    document.removeEventListener("keydown", this.handleKeydown);
    document.removeEventListener("keyup", this.handleKeyup);
    window.removeEventListener("resize", this.getWidowHeight);
  },
  methods: {
    getSummaries({ columns, data }) {
      let sums = [];
      columns.forEach((column, index) => {
        if (!column.property || !this.summaryFields.includes(column.property)) {
          return;
        }
        const values = data.map((item) => Number(item[column.property]));
        if (!values.every((value) => isNaN(value))) {
          sums[index] = values.reduce((prev, curr) => {
            const value = Number(curr);
            if (!isNaN(value)) {
              return prev + curr;
            } else {
              return prev;
            }
          }, 0);
          sums[index] = this.util.numberWComma(sums[index]);
          sums[this.summaryTitleColumnIndex] = "합계";
        }
      });
      return sums;
    },
    initCurrentPage(page) {
      this.currentPage = page ? page : 1;
    },
    getWidowHeight() {
      if (!this.autoHeight) {
        return;
      }
      // this.autoMaxHeight =
      //   document.querySelector("#" + this.id).parentElement.clientHeight - 100;
      // 100 = 상단 검색/버튼 높이 + 하단 페이징/버튼 높이
    },
    parseValue(d, fields) {
      let item = {};
      fields.forEach((f) => {
        if (f.value === "index") {
          item["index"] = this.table_index--;
        } else if (f.formatter) {
          item[f.value] = f.formatter(d, 0, d[f.value]);
        } else {
          item[f.value] = d[f.value];
        }
      });
      return item;
    },
    downloadExcel() {
      let fields = [];
      this.$refs.table.columns.forEach((c) => {
        let value = c.property;
        if (!value && c.index) {
          value = "index";
        }
        if (!c.property && !c.index) {
          return true;
        }
        fields.push({
          label: c.label,
          value: value,
          formatter: c.formatter,
        });
      });
      // let index = this.data.length;
      // let data = this.data.map(function(d) {
      //   let item = {}
      //   fields.forEach(f => {
      //     if (f.value === 'index') {
      //       item['index'] = index--;
      //     } else if (f.formatter) {
      //       item[f.value] = f.formatter(d, 0, d[f.value]);
      //     } else {
      //       item[f.value] = d[f.value];
      //     }
      //   })
      //   return item;
      // });
      /**
       * ERP-645 결산현황 엑셀 저장 문제
       * 각 데이터별 child 데이터가 존재할 경우, map() 방식으로는 child 데이터가 처리되지 못함
       * 그래서, 엑셀 데이터 저장 방식을 map()에서 forEach() 방식으로 변경
       */
      let data = [];
      this.table_index = this.data.length;
      this.data.forEach((d) => {
        data.push(this.parseValue(d, fields));
        if (d.children && d.children.length > 0) {
          d.children.forEach((childrenData) => {
            data.push(this.parseValue(childrenData, fields));
          });
        }
      });
      export2csv.export(data, fields, this.exportName);
    },
    clearSelection() {
      this.$refs.table.clearSelection();
    },
    handleClickSearch() {
      this.currentPage = 1;
      this.fixedSearchWord = this.searchWord;
    },
    handleCellClick() {
      this.$emit("cell-click", ...arguments);
    },
    handleClickRow() {
      this.$emit("row-click", ...arguments);
    },
    handleCurrentPageChange(value) {
      this.currentPage = value;
    },
    handleClickExcel() {
      if (this.exportExcel) {
        this.exportExcel();
        return;
      }
      this.downloadExcel();
    },
    handleSelect(selection, row) {
      this.$nextTick(() => {
        let index = this.$refs.table.data.findIndex((d) => d.id === row.id);
        if (
          this.is_shift &&
          this.last_selection_index > -1 &&
          this.last_selection_index !== index
        ) {
          let from = 0,
            to = 0;
          if (index > this.last_selection_index) {
            from = this.last_selection_index;
            to = index;
          } else if (index < this.last_selection_index) {
            from = index;
            to = this.last_selection_index;
          }
          let targets = this.$refs.table.data.slice(from, to + 1);
          let selection_column = this.$refs.table.columns.find(
            (c) => c.type === "selection"
          );
          targets.forEach((target) => {
            if (selection_column && selection_column.selectable) {
              if (selection_column.selectable(target)) {
                this.$refs.table.toggleRowSelection(target, this.last_selected);
              }
            } else {
              this.$refs.table.toggleRowSelection(target, this.last_selected);
            }
          });
        } else {
          this.last_selection_index = index;
          let s = selection.find((s) => s.id === row.id);
          this.last_selected = s ? true : false;
        }
      });
    },
    handleSelectionChange(selection) {
      this.$emit("selection-change", selection);
    },
    handleCurrentChange() {
      this.$emit("current-change", arguments);
    },
    handleClickCreate() {
      this.$emit("clicked-create");
    },
    indexResolver(index) {
      return this.recordCount - (this.currentPage - 1) * this.pageSize - index;
    },
    toggleRowSelection(row, selected) {
      if (Array.isArray(row)) {
        row.forEach((r) => {
          this.$refs.table.toggleRowSelection(r, selected);
        });
      } else {
        this.$refs.table.toggleRowSelection(row, selected);
      }
    },
    handleSortChange() {
      this.$emit("sort-change", ...arguments);
    },
    setCurrentRow(row) {
      this.$refs.table.setCurrentRow(row);
    },
    handleKeydown(e) {
      var key = e.keyCode;
      if (key === 16) {
        this.is_shift = true;
      }
    },
    handleKeyup() {
      this.is_shift = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.rt-area {
  width: 50px;
}
.lb-area {
  width: 210px;
}
.lb-area {
  > * {
    float: left;
    &:not(:first-child) {
      margin-left: 10px;
    }
  }
}
.search-container {
  margin-left: 10px;
}
</style>
